/* ===================================================================
   useAutomations() + useUpdateAutomation() -- trigger->action rules
   (Brad, 2026-06-17).

   Read:   GET /api/v1/automations
   Update: PUT /api/v1/automations/{id}  (toggle enabled + reconfigure)

   The Automations screen binds to these. After a write we invalidate so
   the rule list reflects the persisted state. HARD RULE: even an enabled
   rule never auto-posts a reply -- any reply action routes through the
   outbox approval gate. Read hook disabled until a Bearer rr_ token exists.
   =================================================================== */
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from '@tanstack/react-query';
import {
  api,
  type Automation,
  type AutomationsResponse,
  type AutomationTrigger,
  type AutomationUpdate,
} from '../lib/api';
import { useApiToken } from './useApiToken';
import { qk } from './queryKeys';

export function useAutomations(): UseQueryResult<AutomationsResponse, Error> {
  const hasToken = Boolean(useApiToken());
  return useQuery({
    queryKey: qk.automations(),
    queryFn: ({ signal }) => api.listAutomations(signal),
    enabled: hasToken,
    staleTime: 30_000,
  });
}

export interface UseUpdateAutomationResult {
  update: (id: string, patch: AutomationUpdate) => Promise<Automation>;
  pendingId: string | null;
  error: Error | null;
}

export function useUpdateAutomation(): UseUpdateAutomationResult {
  const qc = useQueryClient();
  const m = useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: AutomationUpdate }) =>
      api.updateAutomation(id, patch),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.automations() });
    },
  });
  return {
    update: (id, patch) =>
      m.mutateAsync({ id, patch }).then((r) => r.automation),
    pendingId: m.isPending ? (m.variables?.id ?? null) : null,
    error: m.error ?? null,
  };
}

export interface UseCreateAutomationResult {
  create: (input: {
    trigger: AutomationTrigger | string;
    action: string;
    config?: Record<string, unknown>;
    enabled?: boolean;
  }) => Promise<Automation>;
  isPending: boolean;
  error: Error | null;
}

export function useCreateAutomation(): UseCreateAutomationResult {
  const qc = useQueryClient();
  const m = useMutation({
    mutationFn: (input: {
      trigger: AutomationTrigger | string;
      action: string;
      config?: Record<string, unknown>;
      enabled?: boolean;
    }) => api.createAutomation(input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.automations() });
    },
  });
  return {
    create: (input) => m.mutateAsync(input).then((r) => r.automation),
    isPending: m.isPending,
    error: m.error ?? null,
  };
}
